import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Injectable, ViewContainerRef } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, finalize, map, startWith, switchMap, take, tap } from "rxjs/operators";
import { ScheduleUserTask } from "src/app/classes";
import { DefaultNotification } from "src/app/lib/default-notification/classes";
import { ErrorWrapper } from "src/app/lib/error/classes";
import { ErrorDialogComponent } from "src/app/lib/error/components/dialog/error-dialog/error-dialog.component";
import { EMailAudit } from "src/app/lib/mail/classes";
import { IMailProviderService, IMsgProviderService } from "src/app/lib/mail/interfaces";
import { FileViewModes } from "src/app/lib/nextcloud/components/dialog/select-file-view-dialog/select-file-view-dialog.component";
import { IssueTypes } from "src/app/lib/shared/globals";
import { IConcurrencyProviderService, IOffersProviderService } from "src/app/lib/shared/interfaces";
import { DefaultMail, Issue, IssueItem, IssueTransaction, TransformIssueItemsDialogComponentOutputData } from "../../../classes";
import { IIssueProviderService, IIssueTransactionProviderService, IIssueTypeProviderService } from "../../../interfaces";
import { IssueTransactionService } from "../../../services/issue-transaction.service";
import { IssueService } from "../../../services/issue.service";
import { TransformIssueItemsDialogComponent } from "../../dialog/transform-issue-items-dialog/transform-issue-items-dialog.component";

@Injectable()
export class IssueDetailViewComponentService implements IMailProviderService, IIssueTransactionProviderService, IIssueProviderService, IIssueTypeProviderService, IOffersProviderService, IConcurrencyProviderService, IMsgProviderService {

    private _issueId = new ReplaySubject<number>(1);
    issueId$ = this._issueId.asObservable();
    entityIdentifier$ = this.issueId$;

    private _issue = new ReplaySubject<Issue>(1);
    issue$: Observable<Issue> = this._issue.asObservable();

    offers$ = this._issueId.pipe(switchMap(x => this._issueService.getIssueOffers(x)));

    private _mails = new ReplaySubject<EMailAudit[]>(1);
    mails$ = this._mails.asObservable();

    private _issueItems = new ReplaySubject<IssueItem[]>(1);
    issueItems$ = this._issueItems.asObservable();

    issueState$ = this._issue.pipe(map(x => x.issuestate));
    concurrencyStamp$ = this._issue.pipe(map(x => x.concurrencystamp));

    private _loading = new Subject<boolean>();
    loading$ = this._loading.asObservable();

    private _issueTransaction = new ReplaySubject<IssueTransaction>(1);
    issueTransaction$ = this._issueTransaction.asObservable();

    private _issueTransactionAttributes = new ReplaySubject<number[]>(1);
    issueTransactionAttributes$ = this._issueTransactionAttributes.asObservable();

    private _issueTransactionDefaultMails = new ReplaySubject<DefaultMail[]>(1);
    issueTransactionDefaultMails$ = this._issueTransactionDefaultMails.asObservable();

    private _issueTransactionDefaultNotifications = new ReplaySubject<DefaultNotification[]>(1);
    issueTransactionDefaultNotifications$ = this._issueTransactionDefaultNotifications.asObservable();

    private _checkingVersion = new BehaviorSubject(false);
    checkingVersion$ = this._checkingVersion.pipe(tap(() => this._changeDetectorRef.detectChanges()));

    private _msgFiles = new ReplaySubject<{ blobProvider: () => Observable<Blob>, fileName: string }[]>(1);
    msgFiles$ = this._msgFiles.asObservable();

    validStamp$: Observable<boolean> = this._issue.pipe(switchMap(x => {
        this._checkingVersion.next(true);
        return this._issueService.checkIssueVersion(x).pipe(finalize(() => this._checkingVersion.next(false)));
    }));

    nextcloudFolderView = FileViewModes.Tiles;

    formGroup: FormGroup;

    private _currentIssueSub: Subscription = null;
    private _subscriptions: Subscription[] = [];

    constructor(
        private _router: Router,
        private _route: ActivatedRoute,
        private _issueService: IssueService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _dialog: MatDialog,
        private _formBuilder: FormBuilder,
        private _issueTransactionService: IssueTransactionService,
        private _snackBar: MatSnackBar
    ) {
        this.setUp();
    }

    deleteCurrentIssue(): Observable<Object> {
        let subject = new Subject();
        this.issueId$.pipe(take(1), switchMap((issueId: number) => this._issueService.deleteIssue(issueId)))
            .subscribe({
                next: () => {
                    this._router.navigate(['/home']);
                    subject.next();
                },
                error: (error: HttpErrorResponse) => subject.error(error),
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    dispose(): void {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
        this._unsubscribeCurrentIssue();
    }

    getCurrentIssueType(): Observable<number> {
        return this.issue$.pipe(startWith(...[null]), map((x: Issue) => x && x.issuetype ? x.issuetype : IssueTypes.Issue));
    }

    refreshCurrentIssue(): Observable<Issue> {
        let subject = new Subject<Issue>();
        this._issueId.pipe(
            take(1),
            switchMap(x => this._issueService.getIssueById(x, false))
        ).subscribe(subject);
        return subject.asObservable();
    }

    refreshCurrentItems(): Observable<IssueItem[]> {
        let subject = new Subject<IssueItem[]>();
        this._issueId.pipe(
            take(1),
            switchMap(x => this._issueService.getIssueItems(x))
        ).subscribe(subject);
        return subject.asObservable();
    }

    refreshCurrentMails(): Observable<Object> {
        let subject = new Subject();
        this.issueId$.pipe(switchMap((issueId: number) => this._issueService.getIssueMails(issueId)))
            .subscribe({
                next: (result) => {
                    this._mails.next(result);
                    subject.next(result);
                },
                error: (error) => {
                    this._mails.error(error);
                    subject.error(error);
                },
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    saveChanges(issue: Issue = null, note: string = null, scheduleUserTasks: ScheduleUserTask[] = [], issueItems: IssueItem[] = [], deleteIssueItems: number[] = []): Observable<Issue> {
        return this._issueService.updateIssue(issue, note)
            .pipe(
                switchMap((result: Issue) => {
                    if (scheduleUserTasks.length > 0) {
                        return combineLatest(scheduleUserTasks.map(x => this._issueService[x.schedule ? 'assignUser' : 'unassignUser'](x.issue.issueid, x.user.id)))
                            .pipe(map((args) => result));
                    }
                    else return of(result);
                }),
                switchMap((result: Issue) => {
                    if (issueItems.length > 0) {
                        return combineLatest(issueItems.map(x => this._issueService[x.itemid ? 'updateIssueItem' : 'createIssueItem'](x, note)))
                            .pipe(map((args) => result));
                    }
                    else return of(result);
                }),
                switchMap((result: Issue) => {
                    if (deleteIssueItems.length > 0) {
                        return combineLatest(deleteIssueItems.map(x => this._issueService.deleteIssueItem(x))).pipe(map((args) => result));
                    }
                    else return of(result);
                })
            )
    }

    setDefaultNotifications = (defaultNotifications: DefaultNotification[]) => this._issueTransactionDefaultNotifications.next(defaultNotifications);

    setMsgFiles = (files: { fileName: string, blobProvider: () => Observable<Blob> }[]) => this._msgFiles.next(files);

    setUp(): void {

        this.formGroup = this._formBuilder.group({
            issue: this._formBuilder.group(new Issue()),
            issueItems: this._formBuilder.array([]),
            userAssignments: this._formBuilder.array([]),
            isWatched: this._formBuilder.control(false)
        });

        this._subscriptions.push(...[
            this._route.params.pipe(map(params => parseInt(params['id'])), filter(x => typeof (x) === 'number')).subscribe((issueId: number) => this._issueId.next(issueId)),
            this.issueId$.pipe(distinctUntilChanged()).subscribe((identifier) => this.setIssueId(identifier)),
            this.issue$.subscribe(i => console.log(i))
        ]);

    }

    setIssueId(issueId: number) {
        this._loading.next(true);
        this._unsubscribeCurrentIssue();
        this._currentIssueSub = combineLatest([this._issueService.getIssueById(issueId, false), this._issueService.getIssueMails(issueId), this._issueService.getIssueItems(issueId)])
            .subscribe({
                next: ([issue, mails, items]) => {
                    console.log(issue);
                    this._issue.next(issue);
                    this._mails.next(mails);
                    this._issueItems.next(items);
                    this._loading.next(false);
                },
                error: (error: HttpErrorResponse) => {
                    switch (error.status) {
                        case 404:
                            this._dialog.open(ErrorDialogComponent, {
                                panelClass: 'no-padding-dialog-container',
                                disableClose: true,
                                data: {
                                    'description': `Es konnte kein Vorgang mit der ID ${issueId} gefunden werden. Ist der Link eventuell veraltet oder wurde der Vorgang in der Zwischenzeit gelöscht?`,
                                    'error': error,
                                    'showNavigateToMainPage': true
                                } as ErrorWrapper
                            });
                            break;
                    }
                    this._loading.next(false);
                }
            });
    }

    setIssueTransaction(issueTransaction: IssueTransaction) {
        this._issueTransaction.next(issueTransaction);
        if (issueTransaction) {
            this._issueTransactionService.getIssueTransactionAttributes(issueTransaction.guid).subscribe((attributes: number[]) => this._issueTransactionAttributes.next(attributes));
            this._issueTransactionService.getTransactionDefaultMails(issueTransaction.guid).subscribe((defaultMails: DefaultMail[]) => this._issueTransactionDefaultMails.next(defaultMails));
            this._issueTransactionService.getTransactionDefaultNotifications(issueTransaction.guid).subscribe((defaultNotifications: DefaultNotification[]) => this._issueTransactionDefaultNotifications.next(defaultNotifications));
        }
    }

    showBookItemsDialog(viewContainerRef: ViewContainerRef) {
        let subject = new Subject();
        this._dialog.open(TransformIssueItemsDialogComponent, {
            panelClass: 'no-padding-dialog-container',
            viewContainerRef: viewContainerRef,
            autoFocus: false
        }).afterClosed().pipe(
            take(1),
            filter(result => result ? true : false),
            switchMap((result: TransformIssueItemsDialogComponentOutputData) => this._issueService.transformIssueItems(result.map)),
            switchMap(_ => this.refreshCurrentIssue()),
            switchMap(_ => this.refreshCurrentItems())
        ).subscribe({
            next: () => {
                this._snackBar.open(`Die Artikel wurden gebucht`, 'Ok', { duration: 2000 });
                this.formGroup.markAsPristine();
            }
        });
        return subject.asObservable();
    }

    updateIssue(issue: Issue) {
        if (issue) {
            this._issue.next(issue);
            this._issueId.next(issue.issueid);
        }
    }

    private _unsubscribeCurrentIssue() {
        if (this._currentIssueSub) {
            this._currentIssueSub.unsubscribe();
            this._currentIssueSub = null;
        }
    }

}
